import {useId, useMemo, useState} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {useTranslation} from 'react-i18next';

export interface NodeMultiSelectOption {
    value: string;
    label: string;
    /** The heading this option sits under. Grouping turns on as soon as any option carries one. */
    group?: string;
    /** A second line that tells same-label rows apart (e.g. an email); searched, never in the selection summary. */
    secondary?: string;
    color?: string;
}

interface NodeMultiSelectFieldProps {
    label: string;
    value: string[];
    options: NodeMultiSelectOption[];
    onChange: (values: string[]) => void;
    /** id → its stored name snapshot, used when the catalogue lacks the id. */
    unknownLabels?: Record<string, string>;
    /** What an empty selection means, e.g. "Any sector". */
    placeholder?: string;
    /** While true, nothing reads "(no longer available)". */
    loading?: boolean;
    disabled?: boolean;
}

interface InternalOption extends NodeMultiSelectOption {
    unavailable?: boolean;
}

/**
 * Legacy grouped multi-select (new code uses `common/newSelect`). A selected id the options lack keeps its stored
 * name and reads "(no longer available)" only once `loading` is false and there is at least one option.
 */
const NodeMultiSelectField = ({
    label,
    value,
    options,
    onChange,
    unknownLabels,
    placeholder,
    loading,
    disabled
}: NodeMultiSelectFieldProps) => {
    const {t} = useTranslation();
    const summaryId = useId();
    const [inputValue, setInputValue] = useState('');

    // MUI resets the search whenever `value` changes identity, so the derived selection is memoised.
    const {grouped, allOptions, selected} = useMemo(() => {
        const grouped = options.some(option => !!option.group);
        const settled = !loading && options.length > 0;

        // Autocomplete heads each contiguous run, so grouped lists are sorted; ungrouped keep the caller's order.
        const available: InternalOption[] = grouped
            ? [...options].sort((left, right) => {
                const byGroup = (left.group || '').localeCompare(right.group || '');
                return byGroup !== 0 ? byGroup : left.label.localeCompare(right.label);
            })
            : options;

        const unavailable: InternalOption[] = value
            .filter(id => !options.some(option => option.value === id))
            .map(id => {
                const name = unknownLabels?.[id] || id;

                return {
                    value: id,
                    label: settled ? t('{{name}} (no longer available)', {name}) : name,
                    group: settled ? t('No longer available') : undefined,
                    unavailable: settled
                };
            });

        const allOptions = [...available, ...unavailable];
        const selected = value
            .map(id => allOptions.find(option => option.value === id))
            .filter((option): option is InternalOption => !!option);

        return {grouped, allOptions, selected};
    }, [options, value, unknownLabels, loading, t]);

    return (
        <Autocomplete<InternalOption, true, false, false>
            multiple
            disableCloseOnSelect
            size={'small'}
            fullWidth
            disabled={disabled}
            loading={loading}
            loadingText={t('Loading...')}
            noOptionsText={t('No options')}
            options={allOptions}
            value={selected}
            inputValue={inputValue}
            onInputChange={(_event, next) => setInputValue(next)}
            sx={{
                '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                    flexWrap: 'nowrap',
                    height: {xs: 36, sm: 40}
                },
                '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                    minWidth: 24,
                    width: 0
                }
            }}
            groupBy={grouped ? (option => option.group || '') : undefined}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, candidate) => option.value === candidate.value}
            filterOptions={(candidates, {inputValue}) => {
                if (!inputValue) return candidates;

                const needle = inputValue.toLowerCase();
                return candidates.filter(option => (
                    option.label.toLowerCase().includes(needle)
                    || (option.group || '').toLowerCase().includes(needle)
                    || (option.secondary || '').toLowerCase().includes(needle)
                ));
            }}
            onChange={(_event, next) => onChange(next.map(option => option.value))}
            renderOption={(props, option, {selected: checked}) => {
                // Key by option.value; spreading MUI's own (possibly undefined) key prop warns.
                const {key: _key, ...liProps} = props as typeof props & {key?: string};

                return (
                    <Box component={'li'} key={option.value} {...liProps}>
                        <Checkbox
                            checked={checked}
                            size={'small'}
                            tabIndex={-1}
                            disableRipple
                            aria-hidden
                            sx={{p: 0, mr: 1, pointerEvents: 'none'}}
                        />
                        {!!option.color && (
                            <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: option.color, mr: 1, flexShrink: 0}}/>
                        )}
                        <Stack sx={{minWidth: 0}}>
                            <Typography variant={'body1'}>{option.label}</Typography>
                            {!!option.secondary && (
                                <Typography variant={'body2'} color={'text.secondary'} noWrap>
                                    {option.secondary}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                );
            }}
            renderTags={tags => {
                if (inputValue) return null;
                const names = tags.map(option => option.label).join(', ');
                return (
                    <Typography
                        id={summaryId}
                        component={'span'}
                        variant={'body1'}
                        noWrap
                        title={names}
                        color={tags.some(option => option.unavailable) ? 'warning.dark' : 'text.primary'}
                        sx={{maxWidth: 'calc(100% - 24px)', minWidth: 0, pl: 1.25}}
                    >
                        {names}
                    </Typography>
                );
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={selected.length ? undefined : placeholder}
                    InputLabelProps={{...params.InputLabelProps, shrink: true}}
                    inputProps={{
                        ...params.inputProps,
                        'aria-describedby': selected.length && !inputValue
                            ? summaryId
                            : params.inputProps['aria-describedby']
                    }}
                />
            )}
        />
    );
};

export default NodeMultiSelectField;
